import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mic2,
  Award,
  Newspaper,
  Calendar,
  MapPin,
  ExternalLink,
  ArrowRight,
  Sparkles,
  Layers,
  ChevronRight,
  X,
  Share2,
} from 'lucide-react';
import SEO from '../SEO';

const PILLARS = [
  { id: 'all', label: 'All Moments', icon: Layers },
  { id: 'presence', label: 'In Presence', icon: Mic2, subtitle: 'Conferences · Forums · Stages' },
  { id: 'recognition', label: 'Recognition', icon: Award, subtitle: 'Honors · Awards · Milestones' },
  { id: 'published', label: 'Published', icon: Newspaper, subtitle: 'Press · Media · Reports' },
];

const MOMENTS_DATA = [
  // --- IN PRESENCE ---
  {
    id: 'presence-1',
    pillar: 'presence',
    pillarLabel: 'In Presence',
    title: 'State Startup Summit & Innovation Dialogue',
    subtitle: 'Convening on Equitable Venture Capital & Tier-2 Innovation',
    date: '2024',
    location: 'Rajasthan International Centre',
    image: '/events/xlr8ar2.o-1.webp',
    tag: 'Panel Discussion',
    desc: 'InnovHer leaders joined policymakers, investors, and ecosystem builders to discuss bridging the early-stage venture divide and scaling regional founders.',
  },
  {
    id: 'presence-2',
    pillar: 'presence',
    pillarLabel: 'In Presence',
    title: 'D2C Brand Creators & Innovation Roundtable',
    subtitle: 'Direct-to-Consumer Ecosystem Conclave at Studio I',
    date: '2024',
    location: 'Jaipur, Pink City',
    image: '/events/d2c/(4).webp',
    tag: 'Industry Roundtable',
    desc: 'Bringing consumer venture founders, category leaders, and supply chain operators into meaningful proximity to exchange growth playbooks and distribution strategies.',
  },
  {
    id: 'presence-3',
    pillar: 'presence',
    pillarLabel: 'In Presence',
    title: 'Bharat XLR8AR Cohort Showcase & Pitch Stage',
    subtitle: 'Presenting Validated Ventures to Angel Syndicates',
    date: '2024',
    location: 'Vivekanand Global University Hub',
    image: '/events/x2.webp',
    tag: 'Cohort Showcase',
    desc: 'A high-conviction showcase connecting cohort ventures with seasoned investors, advisors, and corporate partners across health, retail, and tech.',
  },
  {
    id: 'presence-4',
    pillar: 'presence',
    pillarLabel: 'In Presence',
    title: 'TiE Global & Regional Chapter Leadership Conclave',
    subtitle: 'Voices of Entrepreneurial Ecosystem Building',
    date: '2023 - 2024',
    location: 'Jaipur & National Chapters',
    image: '/Innovher-home page/2A3A5722.webp',
    tag: 'Keynote & Panel',
    desc: 'Active panel participation addressing institutional venture building, mentor governance, and creating repeatable venture outcomes.',
  },
  {
    id: 'presence-5',
    pillar: 'presence',
    pillarLabel: 'In Presence',
    title: '9 PS Accelerator Series & Masterclasses',
    subtitle: 'Hands-on Venture Mechanics with Domain Leaders',
    date: '2024',
    location: 'Studio I & Collaborative Spaces',
    image: '/events/9 PS Accelerator series/IMG_8970.webp',
    tag: 'Accelerator Workshop',
    desc: 'Deep-dive operational sessions guiding early founders through financial architecture, compliance, product-market validation, and narrative framing.',
  },
  {
    id: 'presence-6',
    pillar: 'presence',
    pillarLabel: 'In Presence',
    title: 'National Women Entrepreneurship Forum',
    subtitle: 'Diversity as the Wellspring of Innovation',
    date: '2023',
    location: 'Rajasthan Innovation Center',
    image: '/events/x1.webp',
    tag: 'Ecosystem Conclave',
    desc: 'Engaging prominent founders and leaders to address the startup ecosystem landscape and advance women, LGBTQ+, and impact entrepreneurs.',
  },

  // --- RECOGNITION ---
  {
    id: 'recognition-1',
    pillar: 'recognition',
    pillarLabel: 'Recognition',
    title: 'Institutional Milestone Celebration',
    subtitle: 'Honoring ₹10Cr+ Ecosystem Soft Commitments & Rapid Cohort Scale',
    date: '2024',
    location: 'State Innovation Corridors',
    image: '/events/celebration.webp',
    tag: 'Milestone Celebration',
    desc: 'Celebrating institutional milestones with ecosystem partners, institutional incubators, and participating founders across Rajasthan and India.',
  },
  {
    id: 'recognition-2',
    pillar: 'recognition',
    pillarLabel: 'Recognition',
    title: 'Excellence in Venture Enablement Award',
    subtitle: 'Honoring Pioneering Work in Tier-2 Ecosystem Building',
    date: '2024',
    location: 'National Entrepreneurship Conclave',
    image: '/events/00001.webp',
    tag: 'Leadership Honor',
    desc: 'Felicitating Dr. Shweta Choudhary and InnovHer for creating inclusive venture infrastructure that translates entrepreneurial ambition into enterprise.',
  },
  {
    id: 'recognition-3',
    pillar: 'recognition',
    pillarLabel: 'Recognition',
    title: 'Flagship Portfolio Acceleration Recognition',
    subtitle: 'Acknowledged by State & University Incubation Partners',
    date: '2024',
    location: 'Jaipur',
    image: '/events/v3.webp',
    tag: 'Institutional Partner Honor',
    desc: 'Recognized by academic and government incubation partners for driving measurable startup traction, diagnostics, and capital mobilization.',
  },
  {
    id: 'recognition-4',
    pillar: 'recognition',
    pillarLabel: 'Recognition',
    title: 'VOSA Futurepreneurs Mentor Felicitations',
    subtitle: 'Nurturing the Next Generation of Youth Innovators',
    date: '2024',
    location: 'Jaipur',
    image: '/events/004.webp',
    tag: 'Youth Innovation Honor',
    desc: 'Honoring InnovHer’s commitment to youth entrepreneurship, evaluating 25+ emerging student ventures with soft investment commitments.',
  },

  // --- PUBLISHED ---
  {
    id: 'published-1',
    pillar: 'published',
    pillarLabel: 'Published',
    title: 'InnovHer in the National News',
    subtitle: 'Leading Dailies Document India’s First Founder-Centric Studio in Pink City',
    date: '2024',
    location: 'Print & Digital Media',
    image: '/stories/NEWS.webp',
    tag: 'Press Feature',
    desc: 'Covered across prominent national business and innovation desks for building structured venture-building processes from a Tier-2 city.',
  },
  {
    id: 'published-2',
    pillar: 'published',
    pillarLabel: 'Published',
    title: 'Unleashing the Power of Diversity: Launch Story',
    subtitle: 'Step into a World Where Innovation Knows No Boundaries',
    date: 'Nov 2023',
    location: 'Editorial Dispatch',
    image: '/stories/innovher1.webp',
    tag: 'Launch Feature',
    desc: 'Documenting the official inauguration at the Rajasthan Innovation Center and outlining the vision to provide mentorship, investment, and network access.',
  },
  {
    id: 'published-3',
    pillar: 'published',
    pillarLabel: 'Published',
    title: 'First Portfolio Showcase & D2C Innovation Insights',
    subtitle: 'A Flagship Showcase of the Inaugural InnovHer Cohort',
    date: '2024',
    location: 'iSSi Publications',
    image: '/stories/innovherhosts3.webp',
    tag: 'Research & Report',
    desc: 'Published ecosystem report detailing early cohort metrics, angel investor participation, and evolving consumer venture trends.',
  },
  {
    id: 'published-4',
    pillar: 'published',
    pillarLabel: 'Published',
    title: 'Medi-Innovate Healthcare Sector Report',
    subtitle: 'Accelerating Medical Innovation & Health-Tech Across Bharat',
    date: '2024',
    location: 'Industry Report',
    image: '/events/x4.webp',
    tag: 'Sector Study',
    desc: 'Editorial publication capturing perspectives from doctors, medical device inventors, and venture partners building healthcare solutions.',
  },
];

export default function FootprintPage() {
  const [activePillar, setActivePillar] = useState('all');
  const [selectedMoment, setSelectedMoment] = useState(null);

  const filteredMoments =
    activePillar === 'all'
      ? MOMENTS_DATA
      : MOMENTS_DATA.filter((m) => m.pillar === activePillar);

  return (
    <div className="bg-[#08080a] text-white min-h-screen selection:bg-[#d42b92]/30">
      <SEO
        title="Footprint | Showing Up, Learning, Contributing"
        description="InnovHer continues to show up across national platforms, industry conversations, entrepreneurial forums and collaborative initiatives. Explore moments of the journey."
        keywords="InnovHer Footprint, InnovHer events, recognition, published, startup events Jaipur, Rajasthan"
        path="/footprint"
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden border-b border-white/5">
        {/* Ambient pink backlights */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[#d42b92]/10 blur-[200px] pointer-events-none" />
        <div className="absolute top-10 right-10 w-[400px] h-[400px] rounded-full bg-[#ff8ec5]/5 blur-[150px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            {/* Eyebrow */}
            {/* <div className="text-[#ff8ec5] text-xs font-semibold tracking-[0.3em] uppercase mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#d42b92] inline-block animate-pulse" />
              <span>FOOTPRINT</span>
            </div> */}

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.06]">
              Showing up. Learning.{' '}
              <span className="text-[#d42b92]">Contributing.</span>
            </h1>

            {/* Subheading Narrative */}
            <p className="mt-8 text-xl sm:text-2xl text-white/90 leading-snug font-medium">
              An ecosystem is built not only through what happens within it, but through how actively it engages with the world around it.
            </p>

            {/* Body Narrative Paragraphs */}
            <div className="mt-6 space-y-5 text-white/80 text-base sm:text-lg leading-relaxed font-normal">
              <p>
                InnovHer continues to show up across national platforms, industry conversations, entrepreneurial forums and collaborative initiatives, participating, learning, sharing perspectives and building relationships along the way.
              </p>
              <p>
                From conferences and panel discussions to media features, awards, recognitions and ecosystem collaborations, every interaction adds another perspective to the journey.
              </p>
              <p className="text-white/90 font-medium">
                These moments reflect more than visibility. They represent the conversations, people, ideas and experiences that continue to shape InnovHer, while allowing InnovHer to contribute its own voice to the evolving innovation ecosystem.
              </p>
            </div>

            {/* Quote Callout Banner */}
            <div className="mt-8 rounded-2xl border border-white/10 bg-gradient-to-r from-[#d42b92]/15 via-white/[0.02] to-transparent p-6 sm:p-7 backdrop-blur-md shadow-lg">
              <div className="flex items-center gap-2 text-xs font-bold text-[#ff8ec5] tracking-widest uppercase mb-1">
                {/* <Sparkles className="w-3.5 h-3.5" /> */}
                <span>The Journey</span>
              </div>
              <p className="text-lg sm:text-xl font-semibold text-white">
                A growing collection of moments from the journey so far.
              </p>
            </div>
          </div>

          {/* Three Pillars Summary Cards */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* 01: In Presence */}
            <div
              onClick={() => setActivePillar('presence')}
              className={`group relative overflow-hidden cursor-pointer rounded-2xl border p-6 transition-all duration-500 ${activePillar === 'presence'
                ? 'border-[#d42b92] shadow-xl shadow-[#d42b92]/20'
                : 'border-white/10 bg-white/[0.02] hover:border-[#d42b92]/50 hover:shadow-lg'
                }`}
            >
              {/* Background Image with Scrim Overlay */}
              <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                <img
                  src="/new_images/presence-bg.webp"
                  alt="In Presence"
                  className="w-full h-full object-cover object-top opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700 ease-out"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080a] via-[#08080a]/85 to-[#08080a]/60" />
                {activePillar === 'presence' && (
                  <div className="absolute inset-0 bg-[#d42b92]/15" />
                )}
              </div>

              {/* Card Content */}
              <div className="relative z-10 flex flex-col h-full justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#d42b92]/25 border border-[#d42b92]/50 flex items-center justify-center text-[#ff8ec5] backdrop-blur-md shadow-md">
                      <Mic2 className="w-5 h-5" />
                    </div>
                    {/* <span className="text-xs font-mono font-bold text-white/60">01</span> */}
                  </div>
                  <h3 className="text-lg font-bold text-white group-hover:text-[#ff8ec5] transition-colors">In Presence</h3>
                  <p className="mt-2 text-xs sm:text-sm text-white/75 leading-relaxed">
                    National stages, summit keynotes, industry dialogues, cohort demo days, and collaborative forums.
                  </p>
                </div>
                <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-[#ff8ec5] group-hover:translate-x-1 transition-transform">
                  <span>Explore Moments</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </div>

            {/* 02: Recognition */}
            <div
              onClick={() => setActivePillar('recognition')}
              className={`group relative overflow-hidden cursor-pointer rounded-2xl border p-6 transition-all duration-500 ${activePillar === 'recognition'
                ? 'border-[#d42b92] shadow-xl shadow-[#d42b92]/20'
                : 'border-white/10 bg-white/[0.02] hover:border-[#d42b92]/50 hover:shadow-lg'
                }`}
            >
              {/* Background Image with Scrim Overlay */}
              <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                <img
                  src="/new_images/recognition-bg.webp"
                  alt="Recognition"
                  className="w-full h-full object-cover object-top opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700 ease-out"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080a] via-[#08080a]/85 to-[#08080a]/60" />
                {activePillar === 'recognition' && (
                  <div className="absolute inset-0 bg-[#d42b92]/15" />
                )}
              </div>

              {/* Card Content */}
              <div className="relative z-10 flex flex-col h-full justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#d42b92]/25 border border-[#d42b92]/50 flex items-center justify-center text-[#ff8ec5] backdrop-blur-md shadow-md">
                      <Award className="w-5 h-5" />
                    </div>
                    {/* <span className="text-xs font-mono font-bold text-white/60">02</span> */}
                  </div>
                  <h3 className="text-lg font-bold text-white group-hover:text-[#ff8ec5] transition-colors">Recognition</h3>
                  <p className="mt-2 text-xs sm:text-sm text-white/75 leading-relaxed">
                    Institutional honors, leadership felicitations, incubator milestones, and ecosystem awards.
                  </p>
                </div>
                <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-[#ff8ec5] group-hover:translate-x-1 transition-transform">
                  <span>Explore Moments</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </div>

            {/* 03: Published */}
            <div
              onClick={() => setActivePillar('published')}
              className={`group relative overflow-hidden cursor-pointer rounded-2xl border p-6 transition-all duration-500 ${activePillar === 'published'
                ? 'border-[#d42b92] shadow-xl shadow-[#d42b92]/20'
                : 'border-white/10 bg-white/[0.02] hover:border-[#d42b92]/50 hover:shadow-lg'
                }`}
            >
              {/* Background Image with Scrim Overlay */}
              <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                <img
                  src="/new_images/published-bg.webp"
                  alt="Published"
                  className="w-full h-full object-cover object-top opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700 ease-out"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080a] via-[#08080a]/85 to-[#08080a]/60" />
                {activePillar === 'published' && (
                  <div className="absolute inset-0 bg-[#d42b92]/15" />
                )}
              </div>

              {/* Card Content */}
              <div className="relative z-10 flex flex-col h-full justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#d42b92]/25 border border-[#d42b92]/50 flex items-center justify-center text-[#ff8ec5] backdrop-blur-md shadow-md">
                      <Newspaper className="w-5 h-5" />
                    </div>
                    {/* <span className="text-xs font-mono font-bold text-white/60">03</span> */}
                  </div>
                  <h3 className="text-lg font-bold text-white group-hover:text-[#ff8ec5] transition-colors">Published</h3>
                  <p className="mt-2 text-xs sm:text-sm text-white/75 leading-relaxed">
                    National press coverage, print features, research publications, and editorial dispatches.
                  </p>
                </div>
                <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-[#ff8ec5] group-hover:translate-x-1 transition-transform">
                  <span>Explore Moments</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Gallery & Exploration Section */}
      <section className="relative py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Interactive Navigation Filter Pills */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
            <div>
              <div className="text-xs font-semibold text-[#ff8ec5] tracking-widest uppercase mb-1">
                Curated Archive
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">
                Moments Across Ecosystems
              </h2>
            </div>

            {/* Filter Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              {PILLARS.map((p) => {
                const Icon = p.icon;
                const active = activePillar === p.id;
                const count =
                  p.id === 'all'
                    ? MOMENTS_DATA.length
                    : MOMENTS_DATA.filter((m) => m.pillar === p.id).length;

                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setActivePillar(p.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold tracking-wider transition-all ${active
                      ? 'bg-[#d42b92] text-white shadow-lg shadow-[#d42b92]/20'
                      : 'border border-white/10 bg-white/[0.03] text-white/70 hover:border-white/25 hover:text-white'
                      }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{p.label}</span>
                    <span
                      className={`ml-1 text-[11px] px-1.5 py-0.2 rounded-full ${active ? 'bg-white/20 text-white' : 'bg-white/10 text-white/60'
                        }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Moments Grid */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {filteredMoments.map((moment) => (
              <div
                key={moment.id}
                onClick={() => setSelectedMoment(moment)}
                className="group relative rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[#d42b92]/60 hover:bg-white/[0.04] transition-all duration-300 overflow-hidden flex flex-col justify-between cursor-pointer shadow-lg hover:shadow-[#d42b92]/15"
              >
                {/* Image Container with Zoom & Badge */}
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-white/[0.05]">
                  <img
                    src={moment.image}
                    alt={moment.title}
                    className="w-full h-full object-cover filter grayscale contrast-105 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#08080a] via-transparent to-black/30 pointer-events-none" />

                  {/* Top Badges */}
                  <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-xs">
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-black/75 backdrop-blur-md border border-white/10 text-[#ff8ec5]">
                      {moment.pillarLabel}
                    </span>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-black/75 backdrop-blur-md border border-white/10 text-white/80">
                      {moment.tag}
                    </span>
                  </div>

                  {/* Bottom Meta in Image */}
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-[11px] text-white/70">
                    <span className="flex items-center gap-1 font-medium">
                      <MapPin className="w-3 h-3 text-[#ff8ec5]" />
                      <span className="truncate max-w-[170px]">{moment.location}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-white/50" />
                      <span>{moment.date}</span>
                    </span>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-5 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white group-hover:text-[#ff8ec5] transition-colors leading-snug">
                      {moment.title}
                    </h3>
                    <p className="mt-1 text-xs font-medium text-white/60 line-clamp-1">
                      {moment.subtitle}
                    </p>
                    <p className="mt-3 text-xs sm:text-[13px] text-white/75 leading-relaxed line-clamp-3">
                      {moment.desc}
                    </p>
                  </div>

                  {/* Action Link */}
                  <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-xs">
                    <span className="text-white/40 text-[11px] uppercase tracking-wider">
                      Moment
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-semibold text-xs text-[#ff8ec5] group-hover:translate-x-1 transition-transform">
                      <span>View Details</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Impact Stats Banner */}
          <div className="mt-16 rounded-2xl border border-white/10 bg-gradient-to-r from-white/[0.04] via-white/[0.02] to-transparent p-8 sm:p-10 backdrop-blur-md">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white">
                  50<span className="text-[#d42b92]">+</span>
                </div>
                <div className="mt-1 text-xs sm:text-sm text-white/60">
                  National Panels &amp; Stages
                </div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white">
                  15<span className="text-[#d42b92]">+</span>
                </div>
                <div className="mt-1 text-xs sm:text-sm text-white/60">
                  Awards &amp; Recognitions
                </div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white">
                  25<span className="text-[#d42b92]">+</span>
                </div>
                <div className="mt-1 text-xs sm:text-sm text-white/60">
                  Media &amp; Press Features
                </div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white">
                  10,000<span className="text-[#d42b92]">+</span>
                </div>
                <div className="mt-1 text-xs sm:text-sm text-white/60">
                  Ecosystem Interactions
                </div>
              </div>
            </div>
          </div>

          {/* Invitation CTA Bar */}
          <div className="mt-16 rounded-3xl border border-white/10 bg-gradient-to-br from-[#d42b92]/20 via-[#0d0d12] to-[#08080a] p-8 sm:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 shadow-2xl">
            <div className="max-w-2xl">
              <span className="text-xs font-bold text-[#ff8ec5] tracking-widest uppercase mb-2 block">
                Ecosystem Engagement
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-white">
                Invite InnovHer to speak, collaborate, or co-host an industry forum.
              </h3>
              <p className="mt-3 text-sm text-white/70 leading-relaxed">
                We actively participate in conferences, accelerator juries, investor roundtables, policy forums, and venture showcases.
              </p>
            </div>
            <Link
              to="/contact?intent=collaborate"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#d42b92] hover:bg-[#e0339f] text-white text-sm font-semibold tracking-wide transition-all shadow-lg shadow-[#d42b92]/30 whitespace-nowrap"
            >
              <span>Connect with Us</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Modal / Lightbox for detailed view */}
      {selectedMoment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/85 backdrop-blur-md">
          <div
            className="relative w-full max-w-2xl rounded-3xl border border-white/15 bg-[#0f0f14] p-6 sm:p-8 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setSelectedMoment(null)}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Modal Image */}
            <div className="relative aspect-[16/9] w-full rounded-2xl overflow-hidden mb-6 border border-white/10">
              <img
                src={selectedMoment.image}
                alt={selectedMoment.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-3 left-3">
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-black/80 backdrop-blur-md border border-white/10 text-[#ff8ec5]">
                  {selectedMoment.pillarLabel}
                </span>
              </div>
            </div>

            {/* Details */}
            <div className="flex items-center gap-4 text-xs text-white/50 mb-2">
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-[#ff8ec5]" />
                <span>{selectedMoment.location}</span>
              </span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>{selectedMoment.date}</span>
              </span>
              <span>·</span>
              <span className="text-[#ff8ec5]">{selectedMoment.tag}</span>
            </div>

            <h3 className="text-xl sm:text-2xl font-bold text-white">
              {selectedMoment.title}
            </h3>
            <p className="mt-1 text-sm font-semibold text-white/70">
              {selectedMoment.subtitle}
            </p>

            <p className="mt-4 text-sm text-white/80 leading-relaxed">
              {selectedMoment.desc}
            </p>

            <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
              <span className="text-xs text-white/40">InnovHer Footprint</span>
              <button
                type="button"
                onClick={() => setSelectedMoment(null)}
                className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
